/**
 * Typed Filing Review models — mirrors backend FilingReviewReport.
 */

export type UxSeverity = 'critical' | 'warning' | 'suggestion';

export type FilingStatus =
  | 'ready_for_filing'
  | 'ready_with_minor_issues'
  | 'requires_revision';

export type LitigationRiskLevel = 'low' | 'medium' | 'high';

export type UxReviewCategory =
  | 'filing_readiness'
  | 'filing_package'
  | 'legal_review'
  | 'facts_chronology'
  | 'annexures_evidence'
  | 'procedural_compliance'
  | 'draft_quality'
  | 'litigation_risk'
  | 'citations'
  | 'cross_document';

export interface ReviewAnchor {
  tab_id?: string | null;
  paragraph_index?: number | null;
  excerpt?: string;
  start_offset?: number | null;
  end_offset?: number | null;
}

export interface ReviewAction {
  id: string;
  label: string;
  prompt_mode?: 'draft' | 'ask' | 'review' | string;
  prompt_template?: string;
  advisory_only?: boolean;
}

export interface FilingReviewFinding {
  id: string;
  category: string;
  code: string;
  title: string;
  reason: string;
  severity: string;
  confidence?: number;
  suggested_improvement?: string;
  affected_paragraphs?: number[];
  related_entities?: string[];
  related_documents?: string[];
  ux_category?: UxReviewCategory | null;
  uxCategory?: UxReviewCategory;
  uxSeverity?: UxSeverity;
  anchor?: ReviewAnchor | null;
  actions?: ReviewAction[];
  package_item_id?: string | null;
  framework_id?: string | null;
}

export interface ScorecardCategory {
  id: UxReviewCategory | string;
  label: string;
  status: 'pass' | 'warn' | 'fail' | string;
  finding_ids?: string[];
  findingIds?: string[];
  summary?: string;
}

export interface FilingReviewReport {
  version: number;
  documentTitle?: string;
  documentType?: string;
  packageId?: string;
  packageLabel?: string;
  status: FilingStatus | string;
  confidence?: number;
  riskLevel?: LitigationRiskLevel | string;
  riskExplanation?: string;
  executiveSummary?: string[];
  scorecard?: ScorecardCategory[];
  findings: FilingReviewFinding[];
  findingCount?: number;
  readinessScore?: number;
  readiness?: {
    overall?: number;
    risk_summary?: string;
    critical_issues?: string[];
    warnings?: string[];
    recommendations?: string[];
  };
  paragraphAssessments?: unknown[];
  stats?: Record<string, unknown>;
  whatWasReviewed?: string[];
}

export const FILING_REVIEW_PROMPT =
  'Run a complete One-Click Filing Review of this document as a senior Pakistani litigation advocate specialising in this case type. Use the deterministic filing-package findings and focus on legal strength, maintainability, jurisdiction, limitation, prayer, annexures, accompaniments (affidavit, memo of address, vakalatnama, index, court fee), and litigation risk. Do not give generic advice.';

export function filingStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'ready_for_filing':
      return 'Ready for Filing';
    case 'ready_with_minor_issues':
      return 'Ready with Minor Issues';
    case 'requires_revision':
      return 'Requires Revision';
    default:
      return status ? status.replace(/_/g, ' ') : 'Review pending';
  }
}

export function findingUxSeverity(f: FilingReviewFinding): UxSeverity {
  if (f.uxSeverity) return f.uxSeverity;
  const s = (f.severity || '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'critical';
  if (s === 'medium') return 'warning';
  return 'suggestion';
}

export function findingUxCategory(f: FilingReviewFinding): UxReviewCategory | string {
  return f.uxCategory || f.ux_category || f.category || 'filing_readiness';
}

export function normalizeFilingReviewReport(raw: Record<string, unknown>): FilingReviewReport {
  const findings = Array.isArray(raw.findings) ? (raw.findings as FilingReviewFinding[]) : [];
  return {
    version: Number(raw.version ?? 2),
    documentTitle: String(raw.documentTitle ?? ''),
    documentType: String(raw.documentType ?? ''),
    packageId: String(raw.packageId ?? ''),
    packageLabel: String(raw.packageLabel ?? ''),
    status: String(raw.status ?? 'requires_revision'),
    confidence: typeof raw.confidence === 'number' ? raw.confidence : 0.8,
    riskLevel: String(raw.riskLevel ?? 'medium'),
    riskExplanation: String(raw.riskExplanation ?? ''),
    executiveSummary: Array.isArray(raw.executiveSummary)
      ? (raw.executiveSummary as string[])
      : [],
    scorecard: Array.isArray(raw.scorecard) ? (raw.scorecard as ScorecardCategory[]) : [],
    findings,
    findingCount: Number(raw.findingCount ?? findings.length),
    readinessScore: typeof raw.readinessScore === 'number' ? raw.readinessScore : undefined,
    readiness: (raw.readiness as FilingReviewReport['readiness']) ?? undefined,
    paragraphAssessments: Array.isArray(raw.paragraphAssessments)
      ? raw.paragraphAssessments
      : [],
    stats: (raw.stats as Record<string, unknown>) ?? undefined,
    whatWasReviewed: Array.isArray(raw.whatWasReviewed)
      ? (raw.whatWasReviewed as string[])
      : [],
  };
}
